// « Agent · 7 conversations · dernière action il y a 12 s » — on every tab.
//
// Owner-approved change to the tab plan (2026-09-25): the agent's pulse sits in
// the top bar so the manager never opens Live just to check it is alive.
//
// ⚠️ TERRACOTTA ONLY WHEN IT IS STUCK. Red on this page means « needs a human
// now », and a frozen agent while a client waits is exactly that. A quiet
// night is calm sage — see lib/liveFeed.ts for what « stale » means.
import { useEffect, useState } from 'react';
import { useT } from '@/lib/i18n';
import type { LiveData } from '@/lib/api';
import { agoLabel } from './workbench';

export function AgentStatusPill({ live, onOpen }: { live: LiveData | undefined; onOpen: () => void }) {
  const { t } = useT();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  if (!live) {
    return (
      <span className="inline-flex items-center gap-2 h-8 px-3 rounded-full border border-border-subtle bg-bg-card text-[12px] text-text-tertiary">
        <span className="w-2 h-2 rounded-full bg-border-default" aria-hidden />
        {t('Agent')} · —
      </span>
    );
  }

  const s = live.status;
  const stale = s.stale;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={t('Voir l’agent en direct')}
      className={`inline-flex items-center gap-2 h-8 px-3 rounded-full border text-[12px] cursor-pointer transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
        stale
          ? 'border-error/40 bg-error/10 text-error hover:bg-error/15'
          : 'border-border-subtle bg-bg-card text-text-secondary hover:bg-bg-elevated'
      }`}
    >
      <span className="relative flex w-2 h-2" aria-hidden>
        {!stale && <span className="absolute inset-0 rounded-full bg-success opacity-60 motion-safe:animate-[storiz-breathe_2.4s_ease-in-out_infinite]" />}
        <span className={`relative w-2 h-2 rounded-full ${stale ? 'bg-error' : 'bg-success'}`} />
      </span>
      {stale ? (
        <span className="font-semibold">
          {t('Agent bloqué — un client attend depuis {w}', { w: agoLabel(s.staleSinceIso, now) })}
        </span>
      ) : (
        <span>
          <b className="font-semibold text-text-primary">{t('Agent')}</b>
          {' · '}
          <span className="tabular-nums">{t('{n} conversation(s)', { n: s.handlingNow })}</span>
          {' · '}
          <span className="tabular-nums">{t('dernière action il y a {w}', { w: agoLabel(s.lastActionIso, now) })}</span>
        </span>
      )}
    </button>
  );
}
